import { useState, useEffect } from "react";
import { Utensils, ArrowRight, Search, X } from "lucide-react";
import type { CategoryWithItemsType } from "../types/category";
import type { CompanyType } from "../types/company";
import { MenuGrid } from "./MenuGrid";
import { useTranslation } from "react-i18next";
import { useCategories } from "@/hooks/useCategories";
import type { ItemType } from "@/types/item";

interface CategoryGridProps {
  initialSelectedCategoryId?: string;
  selectedCompany: CompanyType;
  onItemClick: (item: ItemType) => void;
  onAddToCart: (item: ItemType) => void;
  onScrollFinish?: () => void;
}

export function CategoryGrid({
  initialSelectedCategoryId,
  selectedCompany,
  onItemClick,
  onScrollFinish,
}: CategoryGridProps) {
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryWithItemsType | null>(null);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");

  const { data: categories, isLoading } = useCategories(
    true,
    selectedCompany?.id
  );

  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir(i18n.language) === "rtl";
  const language = i18n.language;

  useEffect(() => {
    if (categories && categories.length > 0) {
      const categoryToSelect =
        categories.find(
          (category) => category.id === initialSelectedCategoryId
        ) || categories[0];

      if (categoryToSelect) {
        setSelectedCategory(categoryToSelect);
        setCategorySearchQuery("");

        setTimeout(() => {
          const categoryElement = document.getElementById("category-section");
          if (categoryElement) {
            categoryElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
          }
          onScrollFinish?.();
        }, 100);
      }
    }
  }, [categories]);

  const handleCategoryClick = (category: CategoryWithItemsType) => {
    setSelectedCategory(category);
    setCategorySearchQuery("");
  };

  const handleCategorySearch = (query: string) => {
    setCategorySearchQuery(query);

    if (!selectedCategory || !selectedCategory.items) {
      return;
    }

    if (!query.trim()) {
      return;
    }

    const filtered = selectedCategory.items.filter(
      (item) =>
        item.name.toLowerCase().includes(query.toLowerCase()) ||
        (item.description &&
          item.description.toLowerCase().includes(query.toLowerCase()))
    );
  };

  const clearCategorySearch = () => {
    setCategorySearchQuery("");
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 h-10 w-24 rounded-full flex-shrink-0"></div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 h-40 sm:h-48 rounded-lg mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (categories && categories.length === 0) {
    return (
      <div className="text-center py-12 sm:py-16 px-4">
        <div className="max-w-md mx-auto">
          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Utensils className="w-10 h-10 sm:w-12 sm:h-12 text-primary" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
            {t("categories.no_categories")}
          </h3>
          <p className="text-gray-500 text-sm sm:text-base">
            {t("categories.check_back")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div id="category-section" className="space-y-8">
      <div className="relative">
        <div className="flex overflow-x-auto pb-2 gap-2 scrollbar-hide">
          {categories &&
            categories.map((category, index) => (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(category)}
                className={`group relative flex items-center space-x-3 px-4 sm:px-6 py-3 sm:py-4 rounded-full transition-all duration-300 flex-shrink-0 ${
                  selectedCategory?.id === category.id
                    ? "bg-primary text-white shadow-lg transform scale-105"
                    : "bg-white text-gray-700 hover:bg-primary-light hover:text-primary shadow-md hover:shadow-lg"
                }`}
                style={{
                  animationDelay: `${index * 50}ms`,
                }}
              >
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                />
                <div className="text-start">
                  <div className="font-semibold text-sm sm:text-base whitespace-nowrap category-name">
                    {category.name}
                  </div>
                  <div
                    className={`text-xs ${
                      selectedCategory?.id === category.id
                        ? "text-primary-light"
                        : "text-gray-500"
                    }`}
                  >
                    {category.items.length || category.items?.length || 0}{" "}
                    {t("categories.items")}
                  </div>
                </div>
                {selectedCategory?.id === category.id &&
                  index !== categories.length - 1 &&
                  (isRTL ? (
                    <ArrowRight className="w-4 h-4 me-2 rotate-180 animate-pulse" />
                  ) : (
                    <ArrowRight className="w-4 h-4 ms-2 animate-pulse" />
                  ))}
              </button>
            ))}
        </div>
      </div>

      {selectedCategory && (
        <div id="category-items" className="space-y-6">
          {/* <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="relative">
              <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={categorySearchQuery}
                onChange={(e) => handleCategorySearch(e.target.value)}
                placeholder={`${t("search.placeholder")} ${
                  selectedCategory.name
                }...`}
                className="block w-full ps-8 sm:ps-10 pe-8 sm:pe-10 py-2 sm:py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary text-sm sm:text-base"
              />
              {categorySearchQuery && (
                <button
                  onClick={clearCategorySearch}
                  className="absolute inset-y-0 end-0 pe-2 sm:pe-3 flex items-center"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            {categorySearchQuery && (
              <p className="mt-2 text-sm text-gray-600">
                {selectedCategory.items.length}{" "}
                {selectedCategory.items.length === 1
                  ? t("categories.item")
                  : t("categories.items")}{" "}
                {t("search.items_found")}
              </p>
            )}
          </div> */}

          {selectedCategory.items && selectedCategory.items.length > 0 ? (
            <MenuGrid
              items={selectedCategory.items}
              onItemClick={onItemClick}
              showTags={true}
            />
          ) : (
            <div className="text-center py-8 px-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Utensils className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500">
                {categorySearchQuery
                  ? t("search.no_items")
                  : t("categories.no_items_in_category")}
              </p>
              {categorySearchQuery && (
                <p className="text-gray-400 mt-2 text-sm">
                  {t("search.try_adjusting")}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
